import { useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Calculator,
  Calendar,
  CalendarClock,
  CheckCircle,
  DollarSign,
  Droplet,
  FileText,
  MoreHorizontal,
  Package,
  Pencil,
  Play,
  Plus,
  Printer,
  Undo2,
  User,
  XCircle,
} from "lucide-react";

import { PalletProductionSection } from "~/components/production";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import { getProductionUnitLabel } from "~/constants/units";
import {
  useBatchIngredientCosts,
  useCancelProductionBatch,
  useProductionBatch,
  useProductionBatchAccounting,
  useRegisterProductionBatchAccounting,
  useReverseOverconsumption,
  useStartProductionBatch,
} from "~/hooks/production";
import { useToast } from "~/hooks/use-toast";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import { useProfileStore } from "~/store/profileStore";
import {
  PRODUCTION_BATCH_STATUS_COLORS,
  PRODUCTION_BATCH_STATUS_LABELS,
} from "~/types/production";
import type { ProductionBatchIngredient } from "~/types/production";
import { formatBatchNumberForDisplay } from "~/utils/batchNumberGenerator";
import { formatCurrency } from "~/utils/helpers";
import { formatUsd } from "~/utils/oilConversions";

import { BatchCompletionDialog } from "./components/BatchCompletionDialog";
import { BatchEditDialog } from "./components/BatchEditDialog";
import { BatchIngredientsTable } from "./components/BatchIngredientsTable";
import { CorrectConsumptionDialog } from "./components/CorrectConsumptionDialog";
import { EditExpiryDialog } from "./components/EditExpiryDialog";
import { MermaDialog } from "./components/MermaDialog";
import { OverconsumptionDialog } from "./components/OverconsumptionDialog";
import { PrintLabelsDialog } from "./components/PrintLabelsDialog";

export function ProductionBatchDetailPage() {
  const navigate = useNavigate();
  const { id: batchId } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { id: userId, role } = useProfileStore();
  const { isProduction } = useCurrentUser();
  const canCorrectConsumption =
    role === "superadmin" || role === "admin" || role === "produccion-admin";

  const { data: batch, isLoading, refetch } = useProductionBatch(batchId);
  const { data: ingredientCosts } = useBatchIngredientCosts(batchId);
  const { data: accounting } = useProductionBatchAccounting(batchId);
  const startMutation = useStartProductionBatch();
  const cancelMutation = useCancelProductionBatch();
  const reverseOverconsumption = useReverseOverconsumption();
  const registerAccounting = useRegisterProductionBatchAccounting();

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showEditExpiryDialog, setShowEditExpiryDialog] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showOverconsumptionDialog, setShowOverconsumptionDialog] =
    useState(false);
  const [showMermaDialog, setShowMermaDialog] = useState(false);
  const [revertPbiId, setRevertPbiId] = useState<string | null>(null);
  const [correctIngredient, setCorrectIngredient] =
    useState<ProductionBatchIngredient | null>(null);

  const handleStart = () => {
    if (!batchId) return;

    startMutation.mutate(batchId, {
      onSuccess: () => {
        toast({
          title: "Producción iniciada",
          description: "El lote ha sido marcado como en progreso",
        });
        refetch();
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleCancel = () => {
    if (!batchId) return;

    cancelMutation.mutate(batchId, {
      onSuccess: () => {
        toast({
          title: "Lote cancelado",
          description: "El lote ha sido cancelado",
        });
        setShowCancelDialog(false);
        refetch();
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleCompletionSuccess = () => {
    setShowCompletionDialog(false);
    refetch();
    toast({
      title: "Lote completado",
      description: "El lote ha sido completado y el stock ha sido actualizado",
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto max-h-full w-[80vw] space-y-6 py-6">
        <Skeleton className="h-12 w-96" />
        <div className="grid grid-cols-3 gap-6">
          <Skeleton className="col-span-2 h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="container mx-auto max-h-full w-[80vw] py-6 text-center">
        <p className="text-muted-foreground">Lote no encontrado</p>
        <Button variant="link" onClick={() => navigate("/production/batches")}>
          Volver a la lista
        </Button>
      </div>
    );
  }

  const canStart = batch.status === "planned" && !isProduction;
  const canComplete = batch.status === "in_progress" && !isProduction;
  const canCancel =
    batch.status !== "completed" &&
    batch.status !== "cancelled" &&
    !isProduction;
  const canRegisterOverconsumption =
    batch.status === "completed" && !isProduction;
  const canRegisterMerma = batch.status !== "cancelled" && !isProduction;
  const canEdit = batch.status === "planned" && !isProduction;
  // Superadmin-only: fix the expiry (vencimiento) after production has started,
  // when the full "Editar" flow is no longer available.
  const canEditExpiry =
    role === "superadmin" &&
    (batch.status === "in_progress" || batch.status === "completed");
  const hasMenuActions =
    canEdit ||
    canEditExpiry ||
    canRegisterOverconsumption ||
    canRegisterMerma ||
    canCancel;

  // Production accounting (Aliace Pruebas test company). The target comprobante
  // page (/contabilidad/movimientos/:id) is admin/superadmin-only, so gate on role.
  const canViewAsiento = role === "superadmin" || role === "admin";
  const showContabilizar =
    canViewAsiento &&
    batch.status === "completed" &&
    accounting?.eligible === true &&
    !accounting?.consumption_asiento_id;
  const asientoLinks =
    canViewAsiento && accounting?.consumption_asiento_id
      ? [
          {
            label: "Consumo de materias primas",
            id: accounting.consumption_asiento_id,
          },
          {
            label: "Ingreso a productos terminados",
            id: accounting.output_asiento_id,
          },
        ]
      : [];

  const handleContabilizar = () => {
    if (!batchId) return;
    registerAccounting.mutate(
      { batchId, createdBy: userId ?? null },
      {
        onSuccess: () => {
          toast({
            title: "Producción contabilizada",
            description:
              "Se registraron los asientos de consumo de MP e ingreso de PT.",
          });
          refetch();
        },
        onError: (error) => {
          toast({
            title: "No se pudo contabilizar",
            description: error.message,
            variant: "destructive",
          });
        },
      }
    );
  };

  // Extra consumption lines recorded on this lote (from record_batch_overconsumption).
  const sobreconsumoRows = (batch.ingredients ?? []).filter(
    (ing) =>
      ing.is_override &&
      ing.recipe_ingredient_id === null &&
      ing.planned_quantity === 0 &&
      (ing.notes ?? "").startsWith("SOBRECONSUMO") &&
      (ing.actual_quantity ?? 0) > 0 &&
      !(ing.notes ?? "").includes("[REVERTIDO]")
  );

  const handleRevertOverconsumption = () => {
    if (!revertPbiId || !batchId) return;
    reverseOverconsumption.mutate(
      { pbiId: revertPbiId, batchId },
      {
        onSuccess: () => {
          toast({
            title: "Sobreconsumo revertido",
            description: "Se restauró el stock y el costo del lote.",
          });
          setRevertPbiId(null);
          refetch();
        },
        onError: (error) => {
          setRevertPbiId(null);
          toast({
            title: "No se pudo revertir",
            description: error.message,
            variant: "destructive",
          });
        },
      }
    );
  };

  return (
    <div className="container mx-auto max-h-full w-[80vw] space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/production/batches")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <Package className="h-6 w-6" />
              {batch.batch_number}
            </h1>
            <p className="text-muted-foreground">
              {formatBatchNumberForDisplay(batch.batch_number)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canStart && (
            <Button
              size="sm"
              onClick={handleStart}
              disabled={startMutation.isPending}
            >
              <Play className="mr-2 h-4 w-4" />
              {startMutation.isPending ? "Iniciando..." : "Iniciar Producción"}
            </Button>
          )}
          {canComplete && (
            <Button size="sm" onClick={() => setShowCompletionDialog(true)}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Completar Lote
            </Button>
          )}
          {showContabilizar && (
            <Button
              size="sm"
              onClick={handleContabilizar}
              disabled={registerAccounting.isPending}
            >
              <Calculator className="mr-2 h-4 w-4" />
              {registerAccounting.isPending
                ? "Contabilizando..."
                : "Contabilizar producción"}
            </Button>
          )}
          {asientoLinks.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  <BookOpen className="mr-2 h-4 w-4" />
                  Apunte contable
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {asientoLinks.map((link) => (
                  <DropdownMenuItem
                    key={link.id ?? link.label}
                    disabled={!link.id}
                    onClick={() =>
                      link.id &&
                      navigate(`/contabilidad/movimientos/${link.id}`)
                    }
                  >
                    <BookOpen className="mr-2 h-4 w-4" />
                    {link.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {hasMenuActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  <MoreHorizontal className="mr-2 h-4 w-4" />
                  Acciones
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (
                  <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                )}
                {canEditExpiry && (
                  <DropdownMenuItem
                    onClick={() => setShowEditExpiryDialog(true)}
                  >
                    <CalendarClock className="mr-2 h-4 w-4" />
                    Editar vencimiento
                  </DropdownMenuItem>
                )}
                {canRegisterOverconsumption && (
                  <DropdownMenuItem
                    onClick={() => setShowOverconsumptionDialog(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Registrar sobreconsumo
                  </DropdownMenuItem>
                )}
                {canRegisterMerma && (
                  <DropdownMenuItem onClick={() => setShowMermaDialog(true)}>
                    <Droplet className="mr-2 h-4 w-4" />
                    Registrar merma
                  </DropdownMenuItem>
                )}
                {canCancel && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600 focus:text-red-700"
                      onClick={() => setShowCancelDialog(true)}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Cancelar lote
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Badge
            className={`${PRODUCTION_BATCH_STATUS_COLORS[batch.status]} px-4 py-1 text-base`}
          >
            {PRODUCTION_BATCH_STATUS_LABELS[batch.status]}
          </Badge>
        </div>
      </div>

      <div
        className={`grid ${isProduction ? "grid-cols-1" : "grid-cols-3"} gap-6`}
      >
        {/* Left column: Details */}
        <div className={`${isProduction ? "" : "col-span-2"} space-y-6`}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Información del Lote</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Producto</p>
                    <p className="font-medium">{batch.product?.short_name}</p>
                    {batch.product?.sku && (
                      <p className="text-sm text-muted-foreground">
                        SKU: {batch.product.sku}
                      </p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground">Receta</p>
                    <p className="font-medium">
                      {batch.recipe?.name || "Sin receta"}
                    </p>
                    {batch.recipe?.oil_type && (
                      <p className="text-sm text-muted-foreground">
                        Aceite: {batch.recipe.oil_type.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground">Cantidad</p>
                    <p className="text-lg font-medium">
                      {batch.actual_quantity ?? batch.target_quantity}{" "}
                      {getProductionUnitLabel(
                        batch.production_unit,
                        batch.actual_quantity ?? batch.target_quantity
                      )}
                    </p>
                    {batch.actual_quantity !== null &&
                      batch.actual_quantity !== batch.target_quantity && (
                        <p className="text-sm text-muted-foreground">
                          Objetivo: {batch.target_quantity}{" "}
                          {getProductionUnitLabel(
                            batch.production_unit,
                            batch.target_quantity
                          )}
                        </p>
                      )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-2">
                    <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Fechas</p>
                      <p className="font-medium">
                        {dayjs(batch.start_date).format("DD/MM/YYYY")} -{" "}
                        {dayjs(batch.end_date).format("DD/MM/YYYY")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Vencimiento
                      </p>
                      <p className="font-medium">
                        {dayjs(batch.expiry_date).format("DD/MM/YYYY")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <User className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Creado por
                      </p>
                      <p className="font-medium">
                        {batch.created_by_profile?.name &&
                        batch.created_by_profile?.lastname
                          ? `${batch.created_by_profile.name} ${batch.created_by_profile.lastname}`
                          : "-"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {dayjs(batch.created_at).format("DD/MM/YYYY HH:mm")}
                      </p>
                    </div>
                  </div>

                  {batch.completed_by_profile && (
                    <div className="flex items-start gap-2">
                      <User className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Completado por
                        </p>
                        <p className="font-medium">
                          {batch.completed_by_profile.name &&
                          batch.completed_by_profile.lastname
                            ? `${batch.completed_by_profile.name} ${batch.completed_by_profile.lastname}`
                            : "-"}
                        </p>
                      </div>
                    </div>
                  )}

                  {batch.assembly_line && (
                    <div className="flex items-start gap-2">
                      <Package className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Línea de Ensamblaje
                        </p>
                        <p className="font-medium">{batch.assembly_line}</p>
                      </div>
                    </div>
                  )}

                  {batch.lot_number && (
                    <div className="flex items-start gap-2">
                      <FileText className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Número de Lote
                        </p>
                        <p className="font-mono font-medium">
                          {batch.lot_number}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-2">
                    <AlertTriangle
                      className={`mt-0.5 h-4 w-4 ${batch.contains_allergens ? "text-orange-500" : "text-muted-foreground"}`}
                    />
                    <div>
                      <p className="text-sm text-muted-foreground">Alergenos</p>
                      <p className="font-medium">
                        {batch.contains_allergens
                          ? "Contiene alergenos"
                          : "Sin alergenos"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {batch.notes && (
                <div className="mt-4 border-t pt-4">
                  <div className="flex items-start gap-2">
                    <FileText className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Notas</p>
                      <p className="font-medium">{batch.notes}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pallet Production Section */}
          {(batch.status === "in_progress" || batch.status === "completed") && (
            <PalletProductionSection batch={batch} />
          )}

          {/* Ingredients table - hidden for production role */}
          {batch.ingredients &&
            batch.ingredients.length > 0 &&
            !isProduction && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Ingredientes</CardTitle>
                </CardHeader>
                <CardContent>
                  <BatchIngredientsTable
                    ingredients={batch.ingredients}
                    showActual={batch.status === "completed"}
                    costsById={ingredientCosts}
                    onCorrect={
                      batch.status === "completed" && canCorrectConsumption
                        ? setCorrectIngredient
                        : undefined
                    }
                  />
                </CardContent>
              </Card>
            )}

          {/* Sobreconsumos registrados sobre este lote (con reversa) */}
          {sobreconsumoRows.length > 0 && !isProduction && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Sobreconsumos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {sobreconsumoRows.map((ing) => {
                  const isOil = !!ing.oil_type_id;
                  const name = isOil
                    ? ing.oil_type?.code
                      ? `${ing.oil_type.code} - ${ing.oil_type.name}`
                      : (ing.oil_type?.name ?? "Aceite")
                    : ing.raw_material?.code
                      ? `${ing.raw_material.code} - ${ing.raw_material.name}`
                      : (ing.raw_material?.name ?? "Material");
                  const unit = isOil ? "kg" : (ing.raw_material?.unit ?? "");
                  return (
                    <div
                      key={ing.id}
                      className="flex items-center justify-between rounded-lg border bg-muted/30 p-3 text-sm"
                    >
                      <div>
                        <span className="font-medium">{name}</span>
                        <span className="ml-2 text-muted-foreground">
                          +{ing.actual_quantity} {unit} · $
                          {formatCurrency(
                            (ing.actual_quantity ?? 0) * ing.unit_cost_clp
                          )}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setRevertPbiId(ing.id)}
                      >
                        <Undo2 className="mr-2 h-4 w-4" />
                        Revertir
                      </Button>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column: Costs and Actions - hidden for production role */}
        {!isProduction && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <DollarSign className="h-4 w-4" />
                  Costos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Tipo de Cambio:
                    </span>
                    <span className="font-medium">
                      {batch.exchange_rate_snapshot
                        ? `$${formatCurrency(batch.exchange_rate_snapshot)}`
                        : "-"}{" "}
                      / USD
                    </span>
                  </div>

                  <hr />

                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Costo Estimado (CLP):
                    </span>
                    <span className="font-medium">
                      {batch.estimated_cost_clp
                        ? `$${formatCurrency(batch.estimated_cost_clp)}`
                        : "-"}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Costo Estimado (USD):
                    </span>
                    <span className="font-medium">
                      {batch.estimated_cost_usd
                        ? formatUsd(batch.estimated_cost_usd)
                        : "-"}
                    </span>
                  </div>

                  {batch.status === "completed" && (
                    <>
                      <hr />

                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          Costo Real (CLP):
                        </span>
                        <span className="font-bold">
                          {batch.actual_cost_clp
                            ? `$${formatCurrency(batch.actual_cost_clp)}`
                            : "-"}
                        </span>
                      </div>

                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          Costo Real (USD):
                        </span>
                        <span className="font-bold">
                          {batch.actual_cost_usd
                            ? formatUsd(batch.actual_cost_usd)
                            : "-"}
                        </span>
                      </div>

                      {batch.actual_cost_clp &&
                        batch.actual_quantity &&
                        batch.actual_quantity > 0 && (
                          <div className="flex justify-between pt-2">
                            <span className="font-medium">
                              Costo por{" "}
                              {batch.production_unit === "UNI"
                                ? "Unidad"
                                : "Caja"}
                              :
                            </span>
                            <span className="font-bold">
                              $
                              {formatCurrency(
                                batch.actual_cost_clp / batch.actual_quantity
                              )}
                            </span>
                          </div>
                        )}

                      {sobreconsumoRows.length > 0 && (
                        <p className="pt-1 text-xs text-muted-foreground">
                          Incluye sobreconsumo registrado sobre la receta.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Lote</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea cancelar el lote "{batch.batch_number}"?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, mantener</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-red-600 hover:bg-red-700"
            >
              Sí, cancelar lote
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Completion Dialog */}
      {batch && userId && (
        <BatchCompletionDialog
          open={showCompletionDialog}
          onOpenChange={setShowCompletionDialog}
          batch={batch}
          userId={userId}
          onSuccess={handleCompletionSuccess}
        />
      )}

      {/* Edit Dialog */}
      {batch && (
        <BatchEditDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          batch={batch}
          onSuccess={() => {
            setShowEditDialog(false);
            refetch();
          }}
        />
      )}

      {/* Edit Expiry Dialog (superadmin-only, post-start) */}
      {batch && (
        <EditExpiryDialog
          open={showEditExpiryDialog}
          onOpenChange={setShowEditExpiryDialog}
          batch={batch}
          onSuccess={() => {
            setShowEditExpiryDialog(false);
            refetch();
          }}
        />
      )}

      {/* Print Labels Dialog */}
      {batch && (
        <PrintLabelsDialog
          open={showPrintDialog}
          onOpenChange={setShowPrintDialog}
          batch={batch}
        />
      )}

      {/* Sobreconsumo Dialog */}
      {batch && (
        <OverconsumptionDialog
          open={showOverconsumptionDialog}
          onOpenChange={setShowOverconsumptionDialog}
          batch={batch}
          onSuccess={() => refetch()}
        />
      )}

      {/* Merma Dialog (tied to this lote) */}
      {batch && (
        <MermaDialog
          open={showMermaDialog}
          onOpenChange={setShowMermaDialog}
          batch={batch}
          onSuccess={() => refetch()}
        />
      )}

      {/* Correct consumption dialog (per ingredient, completed lote) */}
      {batchId && (
        <CorrectConsumptionDialog
          open={!!correctIngredient}
          onOpenChange={(o) => !o && setCorrectIngredient(null)}
          batchId={batchId}
          ingredient={correctIngredient}
          onSuccess={() => {
            setCorrectIngredient(null);
            refetch();
          }}
        />
      )}

      {/* Revert sobreconsumo confirmation */}
      <AlertDialog
        open={!!revertPbiId}
        onOpenChange={(o) => !o && setRevertPbiId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revertir sobreconsumo</AlertDialogTitle>
            <AlertDialogDescription>
              Se restaurará el stock del ingrediente y se descontará su costo del
              lote. ¿Confirmar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reverseOverconsumption.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => {
                e.preventDefault();
                handleRevertOverconsumption();
              }}
              disabled={reverseOverconsumption.isPending}
            >
              Revertir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
